/**
 * DNACore V4 — ResizableImage (TipTap Custom Node)
 * Imagem estilo Word: redimensionar, rotacionar, posicionar (inline/flutuante),
 * z-index (frente/atrás), text-wrap. Persistido como <img> com data-attrs + style
 * para sobreviver a save/load HTML e exports.
 */
import React, { useRef, useState, useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import {
  RotateCw, ArrowUpToLine, ArrowDownToLine, Trash2,
  Move, AlignLeft as WrapL, AlignRight as WrapR, Square, RefreshCw,
} from 'lucide-react';

const DEFAULTS = {
  width: 320,
  height: 'auto',
  rotation: 0,
  floating: false,
  x: 0,
  y: 0,
  zIndex: 1,
  wrap: 'none', // none|left|right|behind
};

// ─── React Component (NodeView) ───
const ImageView = ({ node, updateAttributes, deleteNode, selected, editor }) => {
  const attrs = node.attrs;
  const ref = useRef(null);
  const [drag, setDrag] = useState(null); // {startX, startY, origX, origY}
  const [resize, setResize] = useState(null); // {startX, origW, origH, ratio}
  const [rot, setRot] = useState(null);

  const isFloat = !!attrs.floating;
  const rotation = Number(attrs.rotation) || 0;
  const width = Number(attrs.width) || DEFAULTS.width;
  const height = attrs.height === 'auto' ? 'auto' : Number(attrs.height);
  const zIndex = Number(attrs.zIndex) || 1;
  const wrap = attrs.wrap || 'none';

  // ─── Drag (somente floating) ───
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      updateAttributes({ x: drag.origX + dx, y: drag.origY + dy });
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, updateAttributes]);

  // ─── Resize ───
  useEffect(() => {
    if (!resize) return;
    const onMove = (e) => {
      const dx = e.clientX - resize.startX;
      const newW = Math.max(40, resize.origW + dx);
      const newH = resize.ratio ? Math.round(newW / resize.ratio) : 'auto';
      updateAttributes({ width: newW, height: newH });
    };
    const onUp = () => setResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resize, updateAttributes]);

  // ─── Rotate ───
  useEffect(() => {
    if (!rot) return;
    const onMove = (e) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) + 90;
      updateAttributes({ rotation: Math.round(ang) });
    };
    const onUp = () => setRot(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rot, updateAttributes]);

  const startDrag = (e) => {
    if (!isFloat) return;
    e.preventDefault();
    setDrag({
      startX: e.clientX, startY: e.clientY,
      origX: Number(attrs.x) || 0, origY: Number(attrs.y) || 0,
    });
  };
  const startResize = (e) => {
    e.stopPropagation(); e.preventDefault();
    const img = ref.current?.querySelector('img');
    const ratio = (img && img.naturalWidth && img.naturalHeight) ? (img.naturalWidth / img.naturalHeight) : null;
    setResize({ startX: e.clientX, origW: width, origH: typeof height === 'number' ? height : (img?.offsetHeight || width), ratio });
  };
  const startRotate = (e) => {
    e.stopPropagation(); e.preventDefault();
    setRot({});
  };

  const toggleFloat = () => updateAttributes({ floating: !isFloat, x: 0, y: 0, wrap: !isFloat ? 'none' : 'none' });
  const toFront = () => updateAttributes({ zIndex: zIndex + 1 });
  const toBack = () => updateAttributes({ zIndex: Math.max(-5, zIndex - 1) });
  const cycleWrap = () => {
    // V4.3 PREMIUM: ciclo expandido de wrap modes (Word-like)
    const order = ['none', 'left', 'right', 'behind', 'front', 'top-bottom'];
    const idx = order.indexOf(wrap);
    const next = order[(idx + 1) % order.length];
    updateAttributes({ wrap: next });
  };

  // V4.3 PREMIUM: regenerar imagem via dispatch event para o AssessmentEditor
  const regenerate = () => {
    window.dispatchEvent(new CustomEvent('ae-image-regenerate', {
      detail: { src: attrs.src, prompt: attrs.prompt || attrs.alt || '' },
    }));
  };

  // Wrapper que impede o editor de roubar o foco antes do click do botão da toolbar
  const tbBtn = (handler) => ({
    onMouseDown: (e) => { e.preventDefault(); e.stopPropagation(); },
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); handler(); },
  });

  const wrapStyle = isFloat ? {
    position: 'absolute',
    left: `${attrs.x || 0}px`,
    top: `${attrs.y || 0}px`,
    zIndex,
  } : (wrap === 'left' ? { float: 'left', margin: '0 12px 8px 0', zIndex } :
       wrap === 'right' ? { float: 'right', margin: '0 0 8px 12px', zIndex } :
       wrap === 'behind' ? { position: 'relative', zIndex: -1, marginTop: -8 } :
       wrap === 'front' ? { position: 'relative', zIndex: 50, marginTop: -8 } :
       wrap === 'top-bottom' ? { display: 'block', clear: 'both', margin: '12px auto', zIndex } :
       { display: 'inline-block', zIndex });

  return (
    <NodeViewWrapper
      as="span"
      className={`ae-img-wrap ${selected ? 'is-selected' : ''} ${isFloat ? 'is-float' : ''}`}
      style={wrapStyle}
      data-drag-handle
    >
      <span
        ref={ref}
        className="ae-img-box"
        style={{
          display: 'inline-block',
          width: `${width}px`,
          height: typeof height === 'number' ? `${height}px` : 'auto',
          transform: rotation ? `rotate(${rotation}deg)` : 'none',
          cursor: isFloat ? 'move' : 'default',
        }}
        onMouseDown={startDrag}
      >
        <img
          src={attrs.src}
          alt={attrs.alt || ''}
          draggable={false}
          style={{ width: '100%', height: '100%', display: 'block', userSelect: 'none', pointerEvents: 'none' }}
        />
        {selected && (
          <>
            {/* Resize handle (canto inferior direito) */}
            <span className="ae-img-handle ae-img-resize" onMouseDown={startResize} title="Redimensionar" />
            {/* Rotate handle (topo) */}
            <span className="ae-img-handle ae-img-rotate" onMouseDown={startRotate} title="Rotacionar"><RotateCw size={10} /></span>
            {/* Toolbar contextual */}
            <span className="ae-img-toolbar" contentEditable={false} onMouseDown={(e) => e.stopPropagation()}>
              <button {...tbBtn(toggleFloat)} title={isFloat ? 'Inline' : 'Flutuante'} data-testid="img-toggle-float">
                {isFloat ? <Square size={12} /> : <Move size={12} />}
              </button>
              <button {...tbBtn(cycleWrap)} title={`Wrap: ${wrap}`} data-testid="img-wrap">
                {wrap === 'right' ? <WrapR size={12} /> : <WrapL size={12} />}
              </button>
              <button {...tbBtn(toFront)} title="Frente" data-testid="img-front"><ArrowUpToLine size={12} /></button>
              <button {...tbBtn(toBack)} title="Trás" data-testid="img-back"><ArrowDownToLine size={12} /></button>
              <button {...tbBtn(regenerate)} title="Regenerar" data-testid="img-regenerate"><RefreshCw size={12} /></button>
              <button {...tbBtn(() => deleteNode())} title="Excluir" data-testid="img-delete"><Trash2 size={12} /></button>
            </span>
          </>
        )}
      </span>
    </NodeViewWrapper>
  );
};

// ─── TipTap Node Extension ───
export const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  inline: false,
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      width: { default: DEFAULTS.width, parseHTML: el => parseInt(el.getAttribute('data-w') || el.style?.width || DEFAULTS.width, 10) || DEFAULTS.width },
      height: { default: DEFAULTS.height, parseHTML: el => {
        const h = el.getAttribute('data-h'); if (!h || h === 'auto') return 'auto'; return parseInt(h, 10) || 'auto';
      } },
      rotation: { default: 0, parseHTML: el => Number(el.getAttribute('data-rot') || 0) || 0 },
      floating: { default: false, parseHTML: el => el.getAttribute('data-float') === '1' },
      x: { default: 0, parseHTML: el => Number(el.getAttribute('data-x') || 0) || 0 },
      y: { default: 0, parseHTML: el => Number(el.getAttribute('data-y') || 0) || 0 },
      zIndex: { default: 1, parseHTML: el => Number(el.getAttribute('data-z') || 1) || 1 },
      wrap: { default: 'none', parseHTML: el => el.getAttribute('data-wrap') || 'none' },
      prompt: { default: null, parseHTML: el => el.getAttribute('data-prompt') || null },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      src, alt, width, height, rotation, floating, x, y, zIndex, wrap, prompt, ...rest
    } = HTMLAttributes;
    const isFloat = !!floating;
    const w = Number(width) || DEFAULTS.width;
    const h = (height === 'auto' || !height) ? 'auto' : `${Number(height)}px`;
    const rot = Number(rotation) || 0;
    const z = Number(zIndex) || 1;

    // Construir style inline para sobreviver no export (xhtml2pdf entende parte disso)
    const styleParts = [
      `width:${w}px`,
      h !== 'auto' ? `height:${h}` : '',
      rot ? `transform:rotate(${rot}deg)` : '',
      `z-index:${z}`,
    ];
    if (isFloat) {
      styleParts.push('position:absolute', `left:${Number(x)||0}px`, `top:${Number(y)||0}px`);
    } else if (wrap === 'left') {
      styleParts.push('float:left', 'margin:0 12px 8px 0');
    } else if (wrap === 'right') {
      styleParts.push('float:right', 'margin:0 0 8px 12px');
    } else if (wrap === 'behind') {
      styleParts.push('position:relative', 'z-index:-1');
    }
    const style = styleParts.filter(Boolean).join(';');

    return ['img', mergeAttributes(rest, {
      src, alt: alt || '',
      style,
      'data-w': String(w),
      'data-h': h === 'auto' ? 'auto' : String(Number(height)),
      'data-rot': String(rot),
      'data-float': isFloat ? '1' : '0',
      'data-x': String(Number(x) || 0),
      'data-y': String(Number(y) || 0),
      'data-z': String(z),
      'data-wrap': wrap || 'none',
      'data-prompt': prompt || '',
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },

  addCommands() {
    return {
      setImage: (opts) => ({ commands }) => commands.insertContent({ type: this.name, attrs: opts }),
    };
  },
});

export default ResizableImage;
